import { useMemo } from 'react';
import InputLabel from '@mui/material/InputLabel';
import MuiSelect, { BaseSelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import cn from 'classnames';

import { ID, iOption, NullableID } from '@core/models';

type SelectValue = NullableID | readonly ID[]

type SelectProps<T extends SelectValue> = {
    value: T;
    setValue: (value: T) => void;
    options: Array<iOption>;
    isDisabled?: boolean;
    required?: boolean;
    label?: string;
} & Omit<BaseSelectProps, 'value' | 'onChange'>;

const Select = <T extends SelectValue>({
    id,
    value,
    setValue,
    options,
    isDisabled = false,
    required,
    label,
    className,
    ...selectProps
}: SelectProps<T>) => {
    const menuItems = useMemo(() => {
        return options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
                {option.title}
            </MenuItem>
        ));
    }, [options]);

    const renderSelect = useMemo(
        () => (
            <MuiSelect
                id={id}
                name={id}
                value={value}
                onChange={(e) => {
                    setValue(e.target.value as T);
                }}
                labelId={label}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
                fullWidth
                className="Select"
                classes={{
                    root: 'Select__root',
                    outlined: cn('Select__outlined', className),
                }}
                disabled={isDisabled}
                required={required}
                {...selectProps}
            >
                {menuItems}
            </MuiSelect>
        ),
        [className, id, isDisabled, label, menuItems, setValue, value, required, selectProps]
    );

    if (label) {
        return (
            <FormControl fullWidth variant="standard">
                <InputLabel id={label}>{label}</InputLabel>
                {renderSelect}
            </FormControl>
        );
    }

    return renderSelect;
};

export default Select;
